import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { ppcFarmService, LedgerType } from "../services/ppcFarmService";
import {
  PpcFarmTargetLedgerData,
  PpcFarmCohortLedgerData,
  PpcFarmMailingLedgerData,
  PpcFarmStatistics,
  PpcFarmSortOptions,
} from "../types/ppcFarmTypes";

// Generic ledger data type
type GenericLedgerData =
  | PpcFarmTargetLedgerData
  | PpcFarmCohortLedgerData
  | PpcFarmMailingLedgerData;

// Hook return types
interface UseGenericLedgerDataReturn<T extends GenericLedgerData> {
  data: T[];
  statistics: PpcFarmStatistics | null;
  filterOptions: Record<string, string[]>;
  loading: boolean;
  searchLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    hasMore: boolean;
  };
  // Current state
  filters: Record<string, string>;
  sortOptions: PpcFarmSortOptions;
  // Actions
  setFilters: (filters: Record<string, string>) => void;
  setSortOptions: (sort: PpcFarmSortOptions) => void;
  loadMore: () => void;
  refetch: () => Promise<void>;
  clearCache: () => void;
}

// Hook configuration
interface UseGenericLedgerDataConfig {
  ledgerType: LedgerType;
  pageSize?: number;
  initialFilters?: Record<string, string>;
  initialSort?: PpcFarmSortOptions;
  enableRealTimeUpdates?: boolean;
}

// Default configuration
const DEFAULT_CONFIG = {
  pageSize: 50,
  initialFilters: {},
  enableRealTimeUpdates: false,
};

/**
 * Generic hook for any PPC Farm ledger data with server-side pagination and filtering
 * Uses proper server-side pagination with cursor-based infinite scroll
 */
export const useGenericLedgerData = <T extends GenericLedgerData>(
  config: UseGenericLedgerDataConfig,
): UseGenericLedgerDataReturn<T> => {
  const {
    ledgerType,
    pageSize = DEFAULT_CONFIG.pageSize,
    initialFilters = DEFAULT_CONFIG.initialFilters,
    initialSort,
    enableRealTimeUpdates = DEFAULT_CONFIG.enableRealTimeUpdates,
  } = config;

  // Get default sort based on ledger type - memoized to prevent recreations
  const defaultSort = useMemo((): PpcFarmSortOptions => {
    if (initialSort) return initialSort;

    switch (ledgerType) {
      case LedgerType.TARGET:
        return { field: "dateAdded", direction: "desc" };
      case LedgerType.COHORT:
        return { field: "dateCreated", direction: "desc" };
      case LedgerType.MAILING:
        return { field: "dateEntered", direction: "desc" };
      default:
        return { field: "dateAdded", direction: "desc" };
    }
  }, [ledgerType, initialSort]);

  // STATE MANAGEMENT - Server-side pagination with cursor tracking
  const [data, setData] = useState<T[]>([]);
  const [statistics, setStatistics] = useState<PpcFarmStatistics | null>(null);
  const [filterOptionsState, setFilterOptionsState] = useState<
    Record<string, string[]>
  >({});

  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<
    QueryDocumentSnapshot<DocumentData> | undefined
  >(undefined);

  // Filter and sort state
  const [filters, setFiltersState] =
    useState<Record<string, string>>(initialFilters);
  const [sortOptions, setSortOptionsState] =
    useState<PpcFarmSortOptions>(defaultSort);

  // Debouncing for search
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track if initial load has completed
  const initialLoadRef = useRef(false);

  // Ref to track current lastDoc for loadMore function (avoids stale closure)
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | undefined>(
    undefined,
  );

  // Ref to track previous sort options to prevent unnecessary useEffect triggers
  const prevSortRef = useRef<PpcFarmSortOptions>(defaultSort);

  // Memoize filter options to prevent re-renders of Select components
  const filterOptions = useMemo(() => filterOptionsState, [filterOptionsState]);

  // Calculate pagination info
  const pagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      currentPage,
      totalPages,
      pageSize,
      totalItems,
      hasMore,
    };
  }, [currentPage, totalItems, pageSize, hasMore]);

  /**
   * Load initial data and metadata - stabilized to prevent multiple calls
   */
  const loadInitialData = useCallback(async () => {
    if (initialLoadRef.current) {
      console.log("🚫 Initial load already completed, skipping");
      return;
    }

    console.log("🚀 Starting initial data load for", ledgerType);
    
    try {
      setLoading(true);
      setError(null);

      // Run all operations in parallel for maximum speed
      const [statsResult, filterOptionsResult, dataResult] = await Promise.all([
        ppcFarmService.getLedgerStatistics(ledgerType),
        ppcFarmService.getLedgerFilterOptions(ledgerType),
        ppcFarmService.getLedgerData<T>(ledgerType, {
          filters: {},
          sort: defaultSort,
          pagination: { page: 1, limit: pageSize, total: 0 },
          lastDoc: undefined,
        }),
      ]);

      // Set all state at once
      setStatistics(statsResult);
      setFilterOptionsState(filterOptionsResult);
      setData(dataResult.data);
      setTotalItems(dataResult.totalCount);
      setHasMore(dataResult.hasMore);
      setLastDoc(dataResult.lastDoc);
      lastDocRef.current = dataResult.lastDoc; // Update ref
      setCurrentPage(1);
      setError(null);

      console.log(
        `📊 Initial load complete: ${dataResult.data.length} items loaded, ${dataResult.totalCount} total`,
      );
      initialLoadRef.current = true;
    } catch (err) {
      console.error(`❌ Error loading initial ${ledgerType} data:`, err);
      setError(
        err instanceof Error ? err.message : "Failed to load initial data",
      );
    } finally {
      setLoading(false);
    }
  }, [ledgerType, defaultSort, pageSize]);

  /**
   * Load data with specific filters and sort options (avoids stale closure issues)
   */
  const loadDataWithFilters = useCallback(
    async (
      filtersToUse: Record<string, string>,
      sortToUse: PpcFarmSortOptions,
      resetData: boolean = true,
    ) => {
      try {
        if (resetData) {
          setLoading(true);
        }
        setError(null);

        // Use ref to get current lastDoc value (avoids stale closure)
        const currentLastDoc = resetData ? undefined : lastDocRef.current;
        console.log(
          `🔧 loadDataWithFilters called with resetData: ${resetData}, filters:`,
          filtersToUse,
        );

        const dataResult = await ppcFarmService.getLedgerData<T>(ledgerType, {
          filters: filtersToUse,
          sort: sortToUse,
          pagination: {
            page: resetData ? 1 : currentPage + 1,
            limit: pageSize,
            total: 0,
          },
          lastDoc: currentLastDoc,
        });

        if (resetData) {
          // Replace data for new search/filter/sort
          setData(dataResult.data);
          setCurrentPage(1);
          setLastDoc(dataResult.lastDoc);
          lastDocRef.current = dataResult.lastDoc; // Update ref
          // Always update total count for new queries
          setTotalItems(dataResult.totalCount);
        } else {
          // Append data for infinite scroll
          setData((prev) => {
            // Prevent duplicates by checking IDs
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = dataResult.data.filter(
              (item) => !existingIds.has(item.id),
            );
            return [...prev, ...newItems];
          });
          setCurrentPage((prev) => prev + 1);
          setLastDoc(dataResult.lastDoc);
          lastDocRef.current = dataResult.lastDoc; // Update ref
          // For subsequent pages, only update total count if it's provided (> 0)
          // This preserves the original total count from the first page
          if (dataResult.totalCount > 0) {
            setTotalItems(dataResult.totalCount);
          }
          console.log(
            `🔄 Updated lastDoc for next request: ${dataResult.lastDoc?.id || "none"}`,
          );
        }

        setHasMore(dataResult.hasMore);
        setError(null);

        console.log(
          `📄 Data loaded with filters: ${dataResult.data.length} new items, ${dataResult.totalCount} total, hasMore: ${dataResult.hasMore}`,
        );
      } catch (err) {
        console.error(`❌ Error loading ${ledgerType} data:`, err);
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    },
    [ledgerType, pageSize, currentPage],
  );

  /**
   * Load data with current filters and sort options
   */
  const loadData = useCallback(
    async (resetData: boolean = true) => {
      return loadDataWithFilters(filters, sortOptions, resetData);
    },
    [loadDataWithFilters, filters, sortOptions],
  );

  /**
   * Handle filter changes with proper debouncing for search
   */
  const setFilters = useCallback(
    (newFilters: Record<string, string>) => {
      console.log("🔧 setFilters called with:", newFilters);
      console.log("🔧 Previous filters:", filters);

      // Clear existing timer
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      // Check if only search changed for debouncing
      const searchKey = "search";
      const onlySearchChanged =
        Object.keys(newFilters).every((key) => {
          if (key === searchKey) {
            return newFilters[key] !== filters[key];
          }
          return newFilters[key] === filters[key];
        }) &&
        Object.keys(filters).every((key) => {
          if (key === searchKey) {
            return true;
          }
          return newFilters[key] === filters[key];
        });

      // Always update UI state immediately for responsive feel
      setFiltersState(newFilters);

      if (onlySearchChanged && newFilters[searchKey]) {
        // For search, use debouncing
        console.log("🔍 Search detected, applying debouncing");
        setSearchLoading(true);
        searchTimerRef.current = setTimeout(() => {
          console.log(
            "🔍 Search debounce completed, loading data with filters:",
            newFilters,
          );
          setSearchLoading(false);
          // Use a direct API call with the new filters to avoid stale closure
          loadDataWithFilters(newFilters, sortOptions, true);
        }, 300);
      } else {
        // For dropdown filters, apply immediately
        console.log(
          "📊 Filter change detected, loading data immediately with filters:",
          newFilters,
        );
        // Use a direct API call with the new filters to avoid stale closure
        loadDataWithFilters(newFilters, sortOptions, true);
      }
    },
    [filters, sortOptions, loadDataWithFilters],
  );

  /**
   * Handle sort changes
   */
  const setSortOptions = useCallback((newSort: PpcFarmSortOptions) => {
    setSortOptionsState(newSort);
    // Don't call loadData here - let the useEffect handle it to prevent race conditions
  }, []);

  /**
   * Load more data for infinite scroll with intelligent preloading
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) {
      console.log(
        `📄 loadMore skipped - hasMore: ${hasMore}, loading: ${loading}`,
      );
      return;
    }

    console.log(
      `📄 Starting loadMore - Current page: ${currentPage}, lastDoc: ${lastDocRef.current?.id || "none"}, current data length: ${data.length}`,
    );

    try {
      setLoading(true); // Set loading to prevent multiple simultaneous calls
      setError(null);

      const currentLastDoc = lastDocRef.current;
      const currentFilters = filters; // Use current filter state
      const currentSort = sortOptions; // Use current sort state

      const dataResult = await ppcFarmService.getLedgerData<T>(ledgerType, {
        filters: currentFilters,
        sort: currentSort,
        pagination: { page: currentPage + 1, limit: pageSize, total: 0 },
        lastDoc: currentLastDoc,
      });

      console.log(
        `📄 Received ${dataResult.data.length} new items, hasMore: ${dataResult.hasMore}`,
      );

      // Only update state if we actually got new data
      if (dataResult.data.length > 0) {
        // Append data for infinite scroll
        setData((prev) => {
          // Prevent duplicates by checking IDs
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = dataResult.data.filter(
            (item) => !existingIds.has(item.id),
          );
          console.log(
            `📄 Appending ${newItems.length} new items to existing ${prev.length} items`,
          );
          return [...prev, ...newItems];
        });
        setCurrentPage((prev) => prev + 1);
        setLastDoc(dataResult.lastDoc);
        lastDocRef.current = dataResult.lastDoc; // Update ref
      }

      setHasMore(dataResult.hasMore);
      console.log(
        `📄 LoadMore completed - New data length: ${data.length + dataResult.data.length}, hasMore: ${dataResult.hasMore}`,
      );
    } catch (err) {
      console.error(`❌ Error loading more ${ledgerType} data:`, err);
      setError(err instanceof Error ? err.message : "Failed to load more data");
    } finally {
      setLoading(false); // Always reset loading state
    }
  }, [
    hasMore,
    loading,
    currentPage,
    ledgerType,
    filters,
    sortOptions,
    pageSize,
    data.length,
  ]);

  /**
   * Scroll-based preloading trigger
   * Monitors scroll position and triggers loading when user is 80% down the current data
   */
  const handleScrollPreload = useCallback(() => {
    if (!hasMore || loading) return;

    // Get the table container element
    const tableContainer = document.querySelector(
      '[data-testid="ledger-table-container"]',
    );
    if (!tableContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainer;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Trigger preloading when user has scrolled 80% through current data
    if (scrollPercentage >= 0.8) {
      console.log(
        `📊 Scroll-based preloading triggered at ${Math.round(scrollPercentage * 100)}% scroll`,
      );
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  // Add scroll listener for intelligent preloading
  useEffect(() => {
    const tableContainer = document.querySelector(
      '[data-testid="ledger-table-container"]',
    );
    if (!tableContainer) return;

    // Throttle scroll events for performance
    let scrollTimeout: NodeJS.Timeout;
    const throttledScrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScrollPreload, 100);
    };

    tableContainer.addEventListener("scroll", throttledScrollHandler);

    return () => {
      tableContainer.removeEventListener("scroll", throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [handleScrollPreload]);

  /**
   * Refetch current data
   */
  const refetch = useCallback(async () => {
    initialLoadRef.current = false;
    await loadInitialData();
  }, [loadInitialData]);

  /**
   * Clear all cached data and reload
   */
  const clearCache = useCallback(() => {
    initialLoadRef.current = false;
    setData([]);
    setLastDoc(undefined);
    setCurrentPage(1);
    loadInitialData();
  }, [loadInitialData]);

  // Load data when sort changes only - filters are handled in setFilters to prevent race conditions
  useEffect(() => {
    if (initialLoadRef.current) {
      // Skip this effect if we're in the middle of a search operation
      if (searchTimerRef.current) {
        return; // Search is in progress, don't interfere
      }

      // Only trigger if sort options actually changed (not just dependency updates)
      const prevSort = prevSortRef.current;
      const sortChanged =
        prevSort.field !== sortOptions.field ||
        prevSort.direction !== sortOptions.direction;

      if (sortChanged) {
        console.log(
          "🔄 useEffect triggered loadDataWithFilters for sort change:",
          { prevSort, newSort: sortOptions },
        );
        prevSortRef.current = sortOptions; // Update the ref
        loadDataWithFilters(filters, sortOptions, true);
      }
    }
  }, [sortOptions]); // Only depend on sortOptions to prevent loops

  // Initial data load - only run once with proper guards
  useEffect(() => {
    let mounted = true;
    let loadingStarted = false;

    const runInitialLoad = async () => {
      if (!initialLoadRef.current && mounted && !loadingStarted) {
        loadingStarted = true;
        console.log("🎯 useEffect: Starting initial load");
        await loadInitialData();
      } else {
        console.log("🚫 useEffect: Skipping initial load", {
          initialLoadCompleted: initialLoadRef.current,
          mounted,
          loadingStarted
        });
      }
    };

    runInitialLoad();

    return () => {
      mounted = false;
      console.log("🧹 useEffect cleanup: Component unmounting");
    };
  }, [loadInitialData]); // Include loadInitialData but it's now stable

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  return {
    data,
    statistics,
    filterOptions,
    loading,
    searchLoading,
    error,
    pagination,
    filters,
    sortOptions,
    setFilters,
    setSortOptions,
    loadMore,
    refetch,
    clearCache,
  };
};

// Helper functions

/**
 * Get searchable fields for an item based on ledger type
 */
function getSearchableFields(item: any, ledgerType: LedgerType): string[] {
  switch (ledgerType) {
    case LedgerType.TARGET:
      return [
        item.sellerName || "",
        item.sellerAddress || "",
        item.cohort || "",
        item.addedBy || "",
      ];
    case LedgerType.MAILING:
      return [
        item.mailerId || "",
        item.customUrl || "",
        item.notes || "",
        item.cohort || "",
        item.addressingType || "",
      ];
    case LedgerType.COHORT:
      return [
        item.cohortNumber || "",
        item.yourName || "",
        item.mailerShortDescription || "",
      ];
    default:
      return [];
  }
}

/**
 * Sort data based on sort options and ledger type
 */
function sortData<T>(
  data: T[],
  sort: PpcFarmSortOptions,
  ledgerType: LedgerType,
): T[] {
  return [...data].sort((a, b) => {
    let aValue = (a as any)[sort.field];
    let bValue = (b as any)[sort.field];

    // Handle cohort number sorting numerically
    if (sort.field === "cohortNumber" || sort.field === "cohort") {
      aValue = extractCohortNumber(
        (a as any).cohort || (a as any).cohortNumber || "",
      );
      bValue = extractCohortNumber(
        (b as any).cohort || (b as any).cohortNumber || "",
      );
    }

    // Handle date sorting
    if (sort.field.includes("date") || sort.field.includes("Date")) {
      aValue = aValue?.toDate ? aValue.toDate() : new Date(aValue || 0);
      bValue = bValue?.toDate ? bValue.toDate() : new Date(bValue || 0);
    }

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sort.direction === "asc" ? -1 : 1;
    if (bValue == null) return sort.direction === "asc" ? 1 : -1;

    // Compare values
    let comparison = 0;
    if (aValue < bValue) comparison = -1;
    else if (aValue > bValue) comparison = 1;

    // Apply sort direction
    return sort.direction === "asc" ? comparison : -comparison;
  });
}

/**
 * Extract numeric value from cohort string for proper numerical sorting
 */
function extractCohortNumber(cohortStr: string): number {
  const match = cohortStr.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

// Convenience hooks for specific ledger types

export const useTargetLedgerData = (
  config?: Omit<UseGenericLedgerDataConfig, "ledgerType">,
) => {
  return useGenericLedgerData<PpcFarmTargetLedgerData>({
    ledgerType: LedgerType.TARGET,
    initialFilters: { search: "", cohort: "", addedBy: "" },
    ...config,
  });
};

export const useCohortLedgerData = (
  config?: Omit<UseGenericLedgerDataConfig, "ledgerType">,
) => {
  return useGenericLedgerData<PpcFarmCohortLedgerData>({
    ledgerType: LedgerType.COHORT,
    initialFilters: { search: "", cohort: "", yourName: "" },
    ...config,
  });
};

export const useMailingLedgerData = (
  config?: Omit<UseGenericLedgerDataConfig, "ledgerType">,
) => {
  return useGenericLedgerData<PpcFarmMailingLedgerData>({
    ledgerType: LedgerType.MAILING,
    initialFilters: { search: "", cohort: "", addressingType: "", name: "" },
    ...config,
  });
};
